/**
 * MÀN HÌNH CHỜ (LOADING) — điều khiển thanh tiến trình + ẩn/ hiện khối `#bootLoading`.
 *
 * Người dùng: *"thêm màn hình Load, khi load xong ảnh mới thấy trang chủ, khi vào game, load xong
 * ảnh mới hiện màn hình chiến đấu, loading có progress bar nhé"*.
 *
 * ⚠️ VÌ SAO TÁCH THÀNH MODULE RIÊNG, KHÔNG NHÉT VÀO `hud.ts`/`game.ts`:
 * Trạng thái "đang chờ" có 2 nguồn ĐỘC LẬP (ảnh màn ở trang chủ, và ảnh AI của trận). Nhét vào 2
 * file khác nhau thì mỗi bên tự ẩn/ hiện khối ⇒ tranh nhau (bên này ẩn xong bên kia hiện lại). Một
 * nơi giữ trạng thái là cách duy nhất để "ẩn khi TẤT CẢ xong".
 *
 * ⚠️ VÌ SAO KHÔNG CHỜ PHASER: đo trong repo, boot + nạp art AI mất ~13 giây (sau khi gom lô). Màn
 * chờ này là HTML/CSS thuần nên hiện được NGAY khi HTML parse xong, và tiến trình do JS đẩy vào.
 */

const BAR_ID = 'bootBarFill';
const PCT_ID = 'bootPct';
const STAGE_ID = 'bootStage';

/** Khối đang hiện hay không (để `hide()` gọi nhiều lần vẫn an toàn). */
let visible = true;
/**
 * Số việc CHƯA xong. Không dùng boolean vì có nhiều nguồn chờ song song (ảnh màn + ảnh trận) ⇒
 * boolean sẽ tắt màn chờ khi nguồn ĐẦU TIÊN xong, dù nguồn thứ hai còn đang nạp.
 */
let pending = 0;
/** Phần trăm đã báo cho nguồn đang chạy gần nhất (0..100). */
let pct = 0;

function el(id: string): HTMLElement | null {
  return document.getElementById(id);
}

/**
 * Đẩy tiến trình vào thanh + phần trăm.
 *
 * ⚠️⚠️ CHỈ ĐI TỚI, KHÔNG ĐI LÙI — đo được lỗi thật: khi nguồn "ảnh AI trận" (đang ở 93%) và nguồn
 * "ảnh màn trang chủ" (mới bắt đầu, 13%) cùng ghi thì thanh chạy **93% → 77% → 100%**, nhìn như
 * giật/ hỏng. Nay lấy `max` với giá trị đã hiện ⇒ thanh chỉ tiến, đúng cảm giác "đang tải".
 *
 * (Mỗi nguồn vẫn báo tiến trình RIÊNG của nó; việc gộp là của màn chờ, không phải của nguồn.)
 */
export function setProgress(value: number, stage?: string): void {
  pct = Math.max(pct, Math.max(0, Math.min(100, Math.round(value))));
  const fill = el(BAR_ID);
  const label = el(PCT_ID);
  if (fill) fill.style.width = `${pct}%`;
  if (label) label.textContent = `${pct}%`;
  if (stage) {
    const s = el(STAGE_ID);
    if (s) s.textContent = stage;
  }
}

/**
 * ĐĂNG KÝ MỘT NGUỒN CHỜ. Trả hàm `finish` — gọi khi nguồn đó xong.
 *
 * Cách dùng:
 * ```ts
 * const done = beginTask();
 * await loadSomething(withProgress);
 * done();
 * ```
 * Màn chờ CHỈ ẩn khi MỌI nguồn đã gọi `finish()`.
 */
export function beginTask(): () => void {
  pending++;
  visible = true;
  show();
  let called = false;
  return () => {
    if (called) return; // gọi 2 lần không được trừ 2 lần
    called = true;
    pending = Math.max(0, pending - 1);
    if (pending === 0) hide();
  };
}

/** Hiện màn chờ (nếu đang ẩn). */
export function show(): void {
  const box = el('bootLoading');
  if (!box) return;
  visible = true;
  box.classList.remove('done');
  box.classList.remove('hidden');
}

/**
 * Ẩn màn chờ. Có transition mờ dần nên phải chờ `transitionend` mới `display:none` — nếu đặt
 * `display:none` ngay thì hiệu ứng mờ không chạy và màn hình "giật" một cái.
 */
export function hide(): void {
  const box = el('bootLoading');
  if (!box || !visible) return;
  visible = false;
  setProgress(100);
  box.classList.add('done');
  const finish = (): void => box.classList.add('hidden');
  box.addEventListener('transitionend', finish, { once: true });
  // lưới an toàn: nếu transition không bắn (tab ẩn, `prefers-reduced-motion`), vẫn phải ẩn
  window.setTimeout(finish, 500);
}

/** Còn nguồn nào đang chờ không — dùng cho công cụ kiểm chứng. */
export function isLoading(): boolean {
  return pending > 0;
}
